import moment from "moment";
import { createAction } from "redux-actions";
import { saveAs } from "file-saver";
import Immutable from "immutable";
import { spreadOutDataWithCommasArr } from "util/spreadcommas";
import getFilenameFromContentDisposition from "util/getFilenameFromContentDisposition";
import { apiGet, apiPut } from "util/api";
import downloadPropsSelector from "./selectors";
import * as types from "./constants";
import { colTypeMap, formatDate } from "./components/DownloadCommon";

export const fetchSampleTypesRequest = createAction(
  types.DOWNLOAD_SAMPLES_FETCH_REQUEST
);

export const fetchSampleTypesSuccess = createAction(
  types.DOWNLOAD_SAMPLES_FETCH_SUCCESS
);

export const fetchSampleTypesError = createAction(
  types.DOWNLOAD_SAMPLES_FETCH_ERROR
);

export const advValueChange = createAction(types.DOWNLOAD_ADVSEARCH_VALUE);
export const advOperatorChange = createAction(
  types.DOWNLOAD_ADVSEARCH_OPERATOR
);
export const advColChange = createAction(types.DOWNLOAD_ADVSEARCH_COL);

export const timeZoneChange = createAction(types.DOWNLOAD_TIMEZONE_VALUE);

export const advAdd = createAction(types.DOWNLOAD_ADVSEARCH_ADD);
export const advRemove = createAction(types.DOWNLOAD_ADVSEARCH_REMOVE);

export const advSave = createAction(types.DOWNLOAD_ADVSEARCH_SAVE);

export const setViewChanged = createAction(types.DOWNLOAD_VIEW_CHANGED);

export const setFilterChanged = createAction(types.DOWNLOAD_FILTER_CHANGED);
export const fetchPreviewRequest = createAction(
  types.DOWNLOAD_PREVIEW_FETCH_REQUEST
);
export const fetchPreviewSuccess = createAction(
  types.DOWNLOAD_PREVIEW_FETCH_SUCCESS
);
export const fetchPreviewError = createAction(
  types.DOWNLOAD_PREVIEW_FETCH_ERROR
);

export const fetchMapRequest = createAction(types.DOWNLOAD_MAP_FETCH_REQUEST);
export const fetchMapSuccess = createAction(types.DOWNLOAD_MAP_FETCH_SUCCESS);
export const fetchMapError = createAction(types.DOWNLOAD_MAP_FETCH_ERROR);
export const clearSampleType = createAction(types.DOWNLOAD_CLEAR_SAMPLE_TYPE);
export const setSampleType = createAction(types.DOWNLOAD_SET_SAMPLE_TYPE);
export const fetchAutcompleteFilterRequest = createAction(
  types.DOWNLOAD_AUTOCOMPLETE_FILTER_REQUEST
);
export const fetchAutcompleteFilterSuccess = createAction(
  types.DOWNLOAD_AUTOCOMPLETE_FILTER_SUCCESS
);

export const fetchDisplayColumnsRequest = createAction(
  types.DOWNLOAD_DISPLAY_COLUMNS_REQUEST
);
export const fetchDisplayColumnsSuccess = createAction(
  types.DOWNLOAD_DISPLAY_COLUMNS_SUCCESS
);
export const fetchDisplayColumnsError = createAction(
  types.DOWNLOAD_DISPLAY_COLUMNS_ERROR
);

export const fetchRecordCountRequest = createAction(
  types.DOWNLOAD_RECORD_COUNT_FETCH_REQUEST
);
export const fetchRecordCountSuccess = createAction(
  types.DOWNLOAD_RECORD_COUNT_FETCH_SUCCESS
);
export const fetchRecordCountError = createAction(
  types.DOWNLOAD_RECORD_COUNT_FETCH_ERROR
);

export const fetchMetaDataRequest = createAction(
  types.DOWNLOAD_METADATA_FETCH_REQUEST
);

export const fetchMetaDataSuccess = createAction(
  types.DOWNLOAD_METADATA_FETCH_SUCCESS
);

export const fetchMetaDataError = createAction(
  types.DOWNLOAD_METADATA_FETCH_ERROR
);

export const setSearchText = createAction(types.DOWNLOAD_CHIP_SET_TEXT);
export const setChips = createAction(types.DOWNLOAD_CHIP_SET);

export const setStartDateFilter = createAction(
  types.DOWNLOAD_SET_START_DATE_FILTER
);
export const setEndDateFilter = createAction(
  types.DOWNLOAD_SET_END_DATE_FILTER
);

export const setPreviewRowCount = createAction(
  types.DOWNLOAD_SET_PREVIEW_ROW_COUNT
);

export const toggleAdvancedSearch = createAction(
  types.DOWNLOAD_TOGGLE_ADVANCED
);

export const toggleTimeZone = createAction(types.DOWNLOAD_TOGGLE_TIMEZONE);

export const toggleColumnPicker = createAction(
  types.DOWNLOAD_TOGGLE_COLUMN_PICKER
);

export const rawHeadersOnChange = createAction(
  types.DOWNLOAD_TOGGLE_RAW_HEADERS
);

export const setShowPreview = createAction(types.DOWNLOAD_SET_SHOW_PREVIEW);

export const setFilterOpen = createAction(types.DOWNLOAD_SET_FILTER_OPEN);

export const setInfiniteOffset = createAction(
  types.DOWNLOAD_SET_INFINITE_OFFSET
);
export const fetchExcelFileRequest = createAction(
  types.DOWNLOAD_EXCEL_FILE_FETCH_REQUEST
);
export const fetchExcelFileSuccess = createAction(
  types.DOWNLOAD_EXCEL_FILE_FETCH_SUCCESS
);
export const fetchExcelFileError = createAction(
  types.DOWNLOAD_EXCEL_FILE_FETCH_ERROR
);

export const fetchAutcompleteRequest = createAction(
  types.DOWNLOAD_AUTOCOMPLETE_TREE_FETCH_REQUEST
);

export const fetchAutcompleteSuccess = createAction(
  types.DOWNLOAD_AUTOCOMPLETE_TREE_FETCH_SUCCESS
);
export const fetchAutcompleteError = createAction(
  types.DOWNLOAD_AUTOCOMPLETE_TREE_FETCH_ERROR
);
export const downloadAdvSearchUpdate = createAction(
  types.DOWNLOAD_ADVSEARCH_UPDATE
);

export const fetchFormSearchRequestSuccess = createAction(
  types.FORM_SEARCH_REQUEST_SUCCESS
);

export const fetchFormHistoryRequestSuccess = createAction(
  types.FORM_HISTORY_REQUEST_SUCCESS
);
export const submitFormChangesSuccess = createAction(
  types.SUBMIT_FORM_CHANGES_SUCCESS
);

export const acknowledgeFormContentChanged = createAction(
  types.ACKNOWLEDGE_FORM_CONTENT_CHANGED
);

export const formContentChanged = createAction(types.FORM_CONTENT_CHANGED);

export const setSampleSearch = createAction(types.DOWNLOAD_SET_SAMPLE_SEARCH);

export const fetchSampleTypes = () => async dispatch => {
  dispatch(fetchSampleTypesRequest());
  const sampleCategoryTree = {};
  const url = `/eims/lookup_output_views?include_web=true&limit=-1`;
  const sampleOptionsJSON = await apiGet(url).then(res => res.json());

  sampleOptionsJSON.forEach(row => {
    if (!(row.category in sampleCategoryTree))
      sampleCategoryTree[row.category] = [];
    sampleCategoryTree[row.category].push(row);
  });
  dispatch(fetchSampleTypesSuccess({ sampleOptionsJSON, sampleCategoryTree }));
};

// NOTE: The Redux dev tools don't display ES6 Maps, but the data is there.
export const fetchDisplayColumns = () => async dispatch => {
  dispatch(fetchDisplayColumnsRequest());
  try {
    const displayColumnRows = await apiGet(
      `/eims/lookup_display_columns?fields=database_column,display_column&limit=-1`
    ).then(res => res.json());

    const displayColumnLookup = new Map();
    displayColumnRows.forEach(row => {
      displayColumnLookup.set(row.database_column, row);
    });

    dispatch(fetchDisplayColumnsSuccess(displayColumnLookup));
  } catch (err) {
    dispatch(fetchDisplayColumnsError(new Error(err)));
  }
};

export const fetchDownloader = fileType => async (dispatch, getState) => {
  const {
    lastApiQuery,
    downloadRawHeaders,
    sampleFilter
  } = downloadPropsSelector(getState());

  dispatch(fetchExcelFileRequest());
  try {
    let newQuery = "";

    if (lastApiQuery.includes("?")) {
      newQuery += "&";
    } else newQuery += "?";

    const now = new Date();
    const dateToday = now.toISOString().substring(0, 10);

    // rawheaders doesn't apply to CSV
    newQuery += `format=${fileType}&limit=-1`;

    if (downloadRawHeaders) newQuery += "&rawheaders";

    let fileName;
    const content = await apiGet(lastApiQuery + newQuery);
    if (fileType === "xlsx") {
      // pass through filename from header
      fileName = getFilenameFromContentDisposition(
        content.headers.get("content-disposition")
      );
    } else {
      fileName = `${dateToday}_HakaiData_${sampleFilter.slice(7)}.csv`;
    }

    if (content.ok) {
      const blob = await content.blob();
      await saveAs(blob, fileName);
      dispatch(fetchExcelFileSuccess());
    } else {
      // .catch(err => dispatch(fetchExcelFileError(new Error(err))));}
      dispatch(
        fetchExcelFileError(
          new Error(`${content.status} ${content.statusText}`)
        )
      );
    }
  } catch (err) {
    dispatch(fetchExcelFileError(new Error(err)));
  }
};

export const fetchAutcomplete = () => async (dispatch, getState) => {
  const {
    sampleFilter,
    columnNames,
    autocomplete,
    filtersWithDropDowns
  } = downloadPropsSelector(getState());
  dispatch(fetchAutcompleteRequest());
  const cachingAutoComplete = false;
  const queryFields = filtersWithDropDowns.filter(field =>
    columnNames.includes(field)
  );
  if (!queryFields.length) {
    return;
  }

  const query = [`fields=${queryFields.join()}`, "distinct", "limit=-1"];
  try {
    let autoCompleteRows;

    // dont reload autocompete when switching back to a sample type as it can be slow for big datasets
    if (autocomplete.has(sampleFilter) && cachingAutoComplete) {
      autoCompleteRows = autocomplete.get(sampleFilter).toJS();
    } else {
      autoCompleteRows = await apiGet(
        `/eims/views/output/${sampleFilter.slice(7)}?${query.join("&")}`
      ).then(res => res.json());
    }
    autoCompleteRows = spreadOutDataWithCommasArr(autoCompleteRows);

    dispatch(fetchAutcompleteSuccess(autoCompleteRows));
  } catch (err) {
    dispatch(fetchAutcompleteError(new Error(err)));
  }
};

const retrieveAdvancedFilters = () => (dispatch, getState) => {
  const isADate = str => moment(str, "YYYY-MM-DD", true).isValid();

  const query = [];
  const { advancedFiltersValid, sampleMetaData } = downloadPropsSelector(
    getState()
  );
  advancedFiltersValid.forEach(filter => {
    let colType;
    if (sampleMetaData[filter.col]) {
      colType = sampleMetaData[filter.col].type;
    }
    if (!colType) colType = "text";
    const colTypeCategory = colTypeMap.has(colType)
      ? colTypeMap.get(colType)
      : "text";
    let filterStr;
    const filterVal =
      colTypeCategory === "date" && isADate(filter.value)
        ? formatDate(filter.value)
        : filter.value;
    if (colTypeCategory === "number" || colTypeCategory === "date") {
      if (filter.operator === "is not empty") {
        filterStr = `${filter.col}!=null`;
      } else if (filter.operator === "is empty" || !filterVal) {
        filterStr = `${filter.col}=null`;
      } else filterStr = `${filter.col}${filter.operator}${filterVal}`;
    }

    const filtersMapPos = new Map([
      ["contains", `~~*"%${filterVal}%"`],
      ["is exactly", `~~*"${filterVal}"`],
      ["is empty", `=null`],
      ["begins with", `~~*"${filterVal}%"`],
      ["ends with", `~~*"%${filterVal}"`],
      ["is true", `=true`],
      ["is false", `=false`]
    ]);
    const filterNegate = s => `!${s}`;
    const filtersMapNeg = new Map([
      ["does not contain", filterNegate(filtersMapPos.get("contains"))],
      ["is not", filterNegate(filtersMapPos.get("is exactly"))],
      ["is not empty", filterNegate(filtersMapPos.get("is empty"))],
      ["does not begin with", filterNegate(filtersMapPos.get("begins with"))],
      ["does not end with", filterNegate(filtersMapPos.get("ends with"))],
      ["is not true", `!=true`], // special case as API is using !=true and it should be "is not true"
      ["is not false", `!=false`]
    ]);
    const filtersMap = new Map([...filtersMapPos, ...filtersMapNeg]);
    if (colTypeCategory === "text" || colType === "boolean") {
      if (
        filter.col === "comments" &&
        (filter.operator === "is not empty" || filter.operator === "is empty")
      ) {
        if (filter.operator === "is not empty") {
          filterStr = `${filter.col}!=""`;
        } else if (filter.operator === "is empty") {
          filterStr = `${filter.col}=""`;
        }
      } else filterStr = `${filter.col}${filtersMap.get(filter.operator)}`;
    }
    query.push(encodeURIComponent(filterStr));
  });
  return query.join("&");
};

const retrieveChipFilters = () => (dispatch, getState) => {
  const { chips, sampleMetaData } = downloadPropsSelector(getState());
  // TODO: include date in summary. its not a chip but it can be tacked on
  // decide operator based on field type
  const chipQuery = Object.keys(chips)
    .filter(field => chips[field].length)
    .filter(field => sampleMetaData[field])
    .map(field => {
      let dataType = "text";
      if (sampleMetaData[field] && sampleMetaData[field].type) {
        dataType = sampleMetaData[field].type;
      }
      let operator;
      // these are the types returned by the ?meta request. TODO: use these instead of the fields defined in lookup_display_columns
      if (
        [
          "numeric",
          "boolean",
          "date",
          "timestamp with time zone",
          "smallint",
          "bigint",
          "integer"
        ].includes(dataType)
      )
        operator = "=";
      else operator = "&&";
      return `${field}${operator}{${chips[field]
        .map(e => `"${e}"`)
        .join(",")}}`;
    })
    .join("&");
  return encodeURIComponent(chipQuery);
};
export const retrieveDateFilters = () => (dispatch, getState) => {
  const {
    startDateFilter,
    endDateFilter,
    columnNames
    // chips
  } = downloadPropsSelector(getState());
  const query = [];
  const isADate = str => moment(str, "YYYY-MM-DD", true).isValid();

  if (isADate(startDateFilter) && columnNames.includes("date"))
    query.push(`date>=${startDateFilter}`);

  if (endDateFilter && columnNames.includes("date"))
    query.push(
      `date<${moment(endDateFilter)
        .add(1, "days")
        .format("YYYY-MM-DD")}`
    );
  return query.join("&");
};

const retrieveTimeZoneFilters = () => (dispatch, getState) => {
  const query = [];
  const { timeZone } = downloadPropsSelector(getState());
  if (timeZone) {
    query.push(`timezone=${timeZone}`);
    return query;
  }
  return "";
};

// combined the chips, dates and advanced filters, returning a combined query string
const gatherAllFilters = () => dispatch => {
  const dateQuery = dispatch(retrieveDateFilters());
  const chipFilterString = dispatch(retrieveChipFilters());
  const advancedFilterString = dispatch(retrieveAdvancedFilters());
  const timeZoneFilterString = dispatch(retrieveTimeZoneFilters());

  return [
    dateQuery,
    chipFilterString,
    advancedFilterString,
    timeZoneFilterString
  ]
    .filter(e => e)
    .join("&");
};

export const createAPIQuery = () => (dispatch, getState) => {
  const { sampleFilter } = downloadPropsSelector(getState());

  const query = [];
  const allFilters = dispatch(gatherAllFilters());
  if (allFilters) query.push(allFilters);
  const apiQuery = query.join("&");
  let url = `/eims/views/output/${sampleFilter.slice(7)}`;
  if (apiQuery.length) url += `?${apiQuery}`;

  return url;
};

export const fetchRecordCount = () => async dispatch => {
  const urlBeforeCount = dispatch(createAPIQuery());

  // query gets saved for for 'Options'->'Display API Query'. dont want 'count' in there
  dispatch(fetchRecordCountRequest(urlBeforeCount));

  let url = `${urlBeforeCount}`;
  if (url.includes("?")) url += "&count";
  else url += "?count";

  try {
    const count = await apiGet(url).then(res => res.json());

    if (Number.isInteger(count)) dispatch(fetchRecordCountSuccess(count));
  } catch (err) {
    dispatch(fetchRecordCountError(new Error(err)));
  }
};

export const fetchData = () => async (dispatch, getState) => {
  const { sampleFilter } = downloadPropsSelector(getState());
  const allFilters = dispatch(gatherAllFilters());

  // we strip output_ from the beginning of the string!!!
  const urlPrefix = `/eims/views/output/${sampleFilter.slice(7)}`;

  const query = [];

  if (allFilters) query.push(allFilters);

  query.push(`limit=-1`);

  const lastApiQuery = `${urlPrefix}?${query.join("&")}`;
  dispatch(fetchMapRequest({ lastApiQuery }));

  try {
    const data = await apiGet(lastApiQuery).then(res => res.json());

    dispatch(fetchMapSuccess(data));

    return data;
  } catch (err) {
    dispatch(fetchMapError(new Error(err)));
    throw err;
  }
};

// infiniteParams is for inifinite scrolling to start at offset 0
export const fetchPreview = (infiniteParams = { startRow: 0 }) => async (
  dispatch,
  getState
) => {
  const {
    previewRowCount,
    sampleFilter,
    columnNames,
    // preview grid needs to know how many records in total
    recordCount
  } = downloadPropsSelector(getState());
  const query = [];

  const allFilters = dispatch(gatherAllFilters());
  if (allFilters) query.push(allFilters);

  const urlPrefix = `/eims/views/output/${sampleFilter.slice(7)}`;

  let lastApiQuery = urlPrefix;
  if (query.length) lastApiQuery += `?${query.join("&")}`;

  query.push(`offset=${infiniteParams.startRow}`);
  query.push(`limit=${previewRowCount}`);

  if (infiniteParams.sortModel && infiniteParams.sortModel.length) {
    const sortDir = infiniteParams.sortModel[0].sort === "asc" ? "-" : "";

    query.push(`sort=${sortDir}${infiniteParams.sortModel[0].colId}`);
  } else if (columnNames.includes("date")) query.push(`sort=-date`);

  dispatch(fetchPreviewRequest({ lastApiQuery }));

  try {
    const previewData = await apiGet(
      `${urlPrefix}?${query.join("&")}`
    ).then(res => res.json());

    dispatch(fetchPreviewSuccess());
    infiniteParams.successCallback(previewData, recordCount);
  } catch (err) {
    dispatch(fetchPreviewError(new Error(err)));
  }
};

export const fetchMetaData = () => async (dispatch, getState) => {
  dispatch(fetchMetaDataRequest());

  const { sampleFilter } = downloadPropsSelector(getState());
  const query = ["meta"];
  try {
    const sampleMetaData = await apiGet(
      `/eims/views/output/${sampleFilter.slice(7)}?${query.join("&")}`
    ).then(res => res.json());

    dispatch(fetchMetaDataSuccess({ sampleMetaData }));
  } catch (err) {
    dispatch(fetchMetaDataError(new Error(err)));
  }
};
export const rehydrate = () => async (dispatch, getState) => {
  const { sampleFilter } = downloadPropsSelector(getState());
  await dispatch(fetchSampleTypes());
  if (sampleFilter) {
    await dispatch(fetchMetaData());
    await dispatch(fetchRecordCount());
    await dispatch(setFilterChanged(true));
    await dispatch(fetchAutcomplete());
  }
};

export const updateFiltersFromQuery = urlParams => async dispatch => {
  const filterObjects = [];
  // eslint-disable-next-line no-restricted-syntax
  for (const [key, value] of urlParams.entries()) {
    if (key === "sample") {
      // skip sample
    } else if (key === "startDateFilter") {
      dispatch(setStartDateFilter(Immutable.fromJS(value)));
    } else if (key === "endDateFilter") {
      dispatch(setEndDateFilter(Immutable.fromJS(value)));
    } else if (key.startsWith("filter")) {
      filterObjects.push(Immutable.fromJS(JSON.parse(value)));
    } else {
      dispatch(setChips({ field: key, chips: JSON.parse(value) }));
    }
  }
  if (filterObjects.length > 0) {
    dispatch(downloadAdvSearchUpdate(filterObjects));
  }

  fetchRecordCount();
};

export const fetchFormSearchResults = submissionId => async dispatch => {
  const url = `/eims/forms?submission_id=${submissionId}`;
  const formJSON = await apiGet(url).then(res => res.json());
  dispatch(fetchFormSearchRequestSuccess(formJSON[0]));
};

export const fetchFormHistory = submissionId => async dispatch => {
  const url = `/eims/forms_history/${submissionId}`;
  const formJSON = await apiGet(url).then(res => res.json());
  dispatch(fetchFormHistoryRequestSuccess(formJSON));
};

export const submitFormChanges = (submissionId, formJSON) => async dispatch => {
  const url = `/eims/forms/${submissionId}`;
  const response = await apiPut(url, formJSON).then(res => res.json());
  dispatch(submitFormChangesSuccess(response));
  dispatch(formContentChanged());
  dispatch(fetchFormHistory(submissionId));
};
